import { useState, useCallback, useEffect } from "react";
import {
  HelpCircle,
  FileQuestion,
  Mail,
  Search,
  Phone,
  Clock,
  ChevronDown,
  ChevronUp,
  Ticket,
  RefreshCw,
} from "lucide-react";
import {
  Button,
  Input,
  Form,
  Collapse,
  Tabs,
  Table,
  Select,
  DatePicker,
  Pagination,
  Empty,
  Spin,
} from "antd";
import useAuthStore from "stores/AuthStore/AuthStore";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import {
  createSupportTicket,
  fetchSupportTickets,
  getTicketCounts,
} from "services/supportTickets.service";
import {
  TICKET_STATUS,
  PAGE_SIZE,
  Faqs,
  getTicketColumns,
  getTicketStatCards,
} from "./HelpSupport.helpers";
import TicketDrawer from "./_blocks/TicketDrawer";

const { RangePicker } = DatePicker;

// ─── FAQ & Contact tab ────────────────────────────────────────────────────────

function FaqContactTab() {
  const [search, setSearch] = useState("");
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);

  const filteredFaqs = Faqs().filter(
    (faq) =>
      faq.q.toLowerCase().includes(search.toLowerCase()) ||
      faq.a.toLowerCase().includes(search.toLowerCase())
  );

  const faqItems = filteredFaqs.map((faq, i) => ({
    key: String(i),
    label: <span className="font-medium text-sm">{faq.q}</span>,
    children: <p className="text-sm text-muted-foreground">{faq.a}</p>,
  }));

  const handleContact = async (values) => {
    setSending(true);
    const { errors } = await createSupportTicket({
      data: {
        subject: values.subject,
        body: values.message,
      },
    });
    setSending(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    renderSuccessNotifications({
      title: "Ticket raised!",
      message: "We'll get back to you within 24 hours. Track it in My Tickets.",
    });
    form.resetFields();
  };

  return (
    <div className="space-y-8">
      {/* Search */}
      <div className="max-w-xl mx-auto">
        <Input
          size="large"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for help..."
          prefix={<Search className="h-4 w-4 text-muted-foreground" />}
          allowClear
          style={{ borderRadius: 16 }}
        />
      </div>

      {/* FAQ + Contact */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <h2 className="text-lg font-bold mb-4">Frequently Asked Questions</h2>
          {filteredFaqs.length === 0 ? (
            <div className="rounded-2xl border border-border/50 bg-card p-8 text-center">
              <FileQuestion className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">
                No matching questions found
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Try a different search term
              </p>
            </div>
          ) : (
            <Collapse
              accordion
              items={faqItems}
              expandIcon={({ isActive }) =>
                isActive ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )
              }
              expandIconPosition="end"
              style={{ background: "transparent" }}
            />
          )}
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-card">
            <h3 className="font-bold mb-1">Contact Support</h3>
            <p className="text-xs text-muted-foreground mb-5">
              Can&apos;t find what you need? Raise a support ticket.
            </p>
            <Form form={form} layout="vertical" onFinish={handleContact}>
              <Form.Item
                name="subject"
                label="Subject"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please enter a subject",
                  },
                  { min: 3, message: "At least 3 characters" },
                ]}
              >
                <Input
                  placeholder="Brief description of your issue"
                  maxLength={100}
                  showCount
                />
              </Form.Item>
              <Form.Item
                name="message"
                label="Message"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please enter a message",
                  },
                  {
                    min: 10,
                    max: 1000,
                    message:
                      "At least 10 characters and maximum 1000 characters",
                  },
                ]}
              >
                <Input.TextArea
                  rows={5}
                  placeholder="Describe your issue in detail..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
              <Form.Item noStyle>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={sending}
                  icon={<Mail className="mr-2 h-4 w-4" />}
                >
                  Send Message
                </Button>
              </Form.Item>
            </Form>
          </div>

          <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-card space-y-4">
            <h3 className="font-bold">Get in touch with us</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Email</p>
                  <p
                    className="text-xs text-muted-foreground cursor-pointer hover:text-primary hover:underline"
                    onClick={() =>
                      (window.location.href = "mailto:info@weshoup.com")
                    }
                  >
                    info@weshoup.com
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <Phone className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">Phone</p>
                  <p
                    className="text-xs text-muted-foreground cursor-pointer hover:text-primary hover:underline"
                    onClick={() => (window.location.href = "tel:+919833080782")}
                  >
                    +91 9833080782
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium">Hours</p>
                  <p className="text-xs text-muted-foreground">
                    Mon–Fri, 9am–6pm IST
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── My Tickets tab ───────────────────────────────────────────────────────────

function MyTicketsTab() {
  const { userData } = useAuthStore();
  const accountId = userData?.account_id;

  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    dateRange: null,
  });
  const [selectedTicket, setSelectedTicket] = useState(null);

  const loadCounts = async () => {
    const { data, errors } = await getTicketCounts();
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    setCounts(data);
  };

  const loadTickets = useCallback(
    async (page = 1, currentFilters = filters) => {
      if (!accountId) return;
      setLoading(true);
      const body = {
        service_account_id: accountId,
        current_page: page,
        page_size: PAGE_SIZE,
        ...(currentFilters.status ? { status: currentFilters.status } : {}),
        ...(currentFilters.dateRange?.[0]
          ? { from: currentFilters.dateRange[0].toISOString() }
          : {}),
        ...(currentFilters.dateRange?.[1]
          ? { to: currentFilters.dateRange[1].toISOString() }
          : {}),
      };
      const { data, errors } = await fetchSupportTickets(body);
      setLoading(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        return;
      }
      setTickets(data?.data ?? []);
      setPagination(
        data?.meta?.pagination ?? {
          current_page: page,
          total: 0,
          total_pages: 0,
        }
      );
    },
    [accountId, filters]
  );

  useEffect(() => {
    loadCounts();
    loadTickets(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    loadTickets(1, newFilters);
  };

  const handleRefresh = () => {
    loadCounts();
    loadTickets(pagination.current_page);
  };

  const columns = getTicketColumns({ onSelect: setSelectedTicket });

  return (
    <div className="space-y-6">
      {/* Count cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {getTicketStatCards(counts).map(
          ({ label, value, color, bg, iconBg, iconColor, icon }) => (
            <div
              key={label}
              className={`rounded-2xl border border-border/50 ${bg} p-5 shadow-card`}
            >
              <div className="flex items-start justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {label}
                </span>
                <div
                  className={`h-8 w-8 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0`}
                >
                  {icon}
                </div>
              </div>
              <p className={`text-3xl font-bold ${color}`}>{value}</p>
            </div>
          )
        )}
      </div>

      {/* Filters + Refresh */}
      <div className="flex flex-wrap gap-3 items-center justify-between rounded-2xl border border-border/50 bg-card p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <Select
            allowClear
            placeholder="Filter by status"
            style={{ width: 160 }}
            value={filters.status}
            onChange={(val) => handleFilterChange({ ...filters, status: val })}
            options={Object.values(TICKET_STATUS).map((s) => ({
              value: s,
              label: s,
            }))}
          />
          <RangePicker
            value={filters.dateRange}
            onChange={(range) =>
              handleFilterChange({ ...filters, dateRange: range })
            }
            format="MMM D, YYYY"
            placeholder={["From date", "To date"]}
            style={{ borderRadius: 8 }}
          />
        </div>
        <Button
          icon={<RefreshCw className="h-3.5 w-3.5" />}
          onClick={handleRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-border/50 bg-card overflow-hidden shadow-card">
        {loading && tickets.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Spin size="large" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="py-16">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-sm text-muted-foreground">
                  No tickets found. Raise one from the FAQ & Contact tab.
                </span>
              }
            />
          </div>
        ) : (
          <>
            <Table
              dataSource={tickets}
              columns={columns}
              rowKey="ticket_id"
              pagination={false}
              loading={loading}
              onRow={(row) => ({
                onClick: () => setSelectedTicket(row),
                style: { cursor: "pointer" },
              })}
              size="middle"
            />
            {pagination.total_pages > 1 && (
              <div className="flex justify-center py-4 border-t border-border/50">
                <Pagination
                  current={pagination.current_page}
                  total={pagination.total}
                  pageSize={PAGE_SIZE}
                  onChange={(page) => loadTickets(page)}
                  showSizeChanger={false}
                  showTotal={(total) => `${total} tickets`}
                />
              </div>
            )}
          </>
        )}
      </div>

      <TicketDrawer
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
      />
    </div>
  );
}

// ─── Page root ────────────────────────────────────────────────────────────────

function HelpSupport() {
  const [activeTab, setActiveTab] = useState("faq");
  const tabItems = [
    {
      key: "faq",
      label: (
        <span className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4" /> FAQ & Contact
        </span>
      ),
      children: <FaqContactTab />,
    },
    {
      key: "tickets",
      label: (
        <span className="flex items-center gap-2">
          <Ticket className="h-4 w-4" /> My Tickets
        </span>
      ),
      children: <MyTicketsTab />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 mb-4">
          <HelpCircle className="h-7 w-7 text-primary" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Help & Support</h1>
        <p className="text-muted-foreground mt-2">
          Find answers to common questions or raise a support ticket
        </p>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />
    </div>
  );
}

export default HelpSupport;
